// Google's internal HTML-translation endpoint (same one the Translate website widget
// uses). It translates HTML directly, preserving tags/attributes, and accepts an array
// of HTML strings per request. No billing, but undocumented: the API key below is
// Google's public web key and the endpoint can rate-limit (HTTP 429) or change anytime.
const TRANSLATE_HTML_URL = 'https://translate-pa.googleapis.com/v1/translateHtml';
const GOOGLE_PUBLIC_API_KEY = 'AIzaSyATBXajvzQLTDHEQbcpq0Ihe0vWDHmO520';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'NOSCRIPT']);
// Requests succeed comfortably up to ~33KB and start getting rejected (429) around 45KB,
// so we keep each request well under that ceiling.
const MAX_BATCH_CHARS = 15000;
const CONCURRENCY = 4;
const MAX_RETRIES = 3;

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Send a batch of HTML strings in one request; returns the translated strings in order.
// Retries with exponential backoff on rate limiting.
async function translateChunks(chunks, targetLang, attempt = 0) {
  const body = JSON.stringify([[chunks, 'auto', targetLang], 'te_lib']);
  const res = await fetch(TRANSLATE_HTML_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json+protobuf',
      'X-Goog-Api-Key': GOOGLE_PUBLIC_API_KEY,
    },
    body,
  });
  if (res.status === 429 && attempt < MAX_RETRIES) {
    await delay(500 * 2 ** attempt);
    return translateChunks(chunks, targetLang, attempt + 1);
  }
  if (!res.ok) throw new Error(`Translate request failed: ${res.status}`);
  const data = await res.json();
  return data?.[0] || [];
}

async function runWithConcurrency(items, worker, concurrency) {
  let cursor = 0;
  const runners = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (cursor < items.length) {
        const i = cursor;
        cursor += 1;
        // eslint-disable-next-line no-await-in-loop
        await worker(items[i], i);
      }
    },
  );
  await Promise.all(runners);
}

const closeTag = (el) => `</${el.tagName.toLowerCase()}>`;

function openTag(el) {
  const attrs = Array.from(el.attributes)
    .map((attr) => ` ${attr.name}="${attr.value.replace(/"/g, '&quot;')}"`)
    .join('');
  return `<${el.tagName.toLowerCase()}${attrs}>`;
}

// Flatten nodes into ordered chunks. Each chunk is either translatable or passthrough
// (left untouched). Blocks larger than the budget are split into open tag + children +
// close tag so the wrapper survives and the children get translated within the limit.
function collectChunks(nodes, budget, out) {
  nodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push({ html: node.nodeValue, translate: Boolean(node.nodeValue.trim()) });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return; // skip comments etc.

    const serialized = node.outerHTML;
    if (SKIP_TAGS.has(node.tagName)) {
      out.push({ html: serialized, translate: false });
      return;
    }
    if (serialized.length <= budget || !node.childNodes.length) {
      out.push({ html: serialized, translate: true });
      return;
    }
    out.push({ html: openTag(node), translate: false });
    collectChunks(Array.from(node.childNodes), budget, out);
    out.push({ html: closeTag(node), translate: false });
  });
}

// Group chunk indices into batches whose combined HTML length stays under maxChars.
function buildBatches(indices, lengthOf, maxChars) {
  const batches = [];
  let current = [];
  let len = 0;
  indices.forEach((idx) => {
    const cost = lengthOf(idx);
    if (current.length && len + cost > maxChars) {
      batches.push(current);
      current = [];
      len = 0;
    }
    current.push(idx);
    len += cost;
  });
  if (current.length) batches.push(current);
  return batches;
}

// True if the node sits inside a tag we never translate, or a subtree the page has
// opted out of via [data-no-translate] / [translate="no"].
function isSkippable(node, root) {
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.hasAttribute('data-no-translate') || el.getAttribute('translate') === 'no') return true;
    if (el === root) break;
    el = el.parentElement;
  }
  return false;
}

// Translate every visible text node under a live DOM element in place. We only rewrite
// text-node values (never structure), so React's element tree and event handlers stay
// intact and the translation survives re-renders. Returns records used by
// restoreElement() to switch back to the original language without a re-fetch.
export async function translateElement(root, targetLang = 'hi') {
  if (typeof window === 'undefined' || !root) return [];

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes = [];
  let node = walker.nextNode();
  while (node) {
    if (node.nodeValue && node.nodeValue.trim() && !isSkippable(node, root)) {
      nodes.push(node);
    }
    node = walker.nextNode();
  }

  const records = nodes.map((n) => ({ node: n, original: n.nodeValue }));
  const texts = nodes.map((n) => n.nodeValue.replace(/\s+/g, ' ').trim());
  // Reused element to decode HTML entities the endpoint may return (e.g. &amp; -> &).
  const decoder = document.createElement('textarea');
  const decode = (s) => { decoder.innerHTML = s; return decoder.value; };

  const batches = buildBatches(texts.map((_, i) => i), (i) => texts[i].length, MAX_BATCH_CHARS);
  await runWithConcurrency(
    batches,
    async (indices) => {
      try {
        const out = await translateChunks(indices.map((i) => texts[i]), targetLang);
        indices.forEach((idx, k) => {
          const translated = out[k];
          if (typeof translated !== 'string') return;
          const { original } = records[idx];
          const lead = original.match(/^\s*/)?.[0] || '';
          const trail = original.match(/\s*$/)?.[0] || '';
          records[idx].node.nodeValue = `${lead}${decode(translated).trim()}${trail}`;
        });
      } catch {
        // Leave this batch's nodes in their original language on failure.
      }
    },
    CONCURRENCY,
  );

  return records;
}

// Restore the original text captured by translateElement().
export function restoreElement(records) {
  records.forEach((record) => {
    // eslint-disable-next-line no-param-reassign
    record.node.nodeValue = record.original;
  });
}

export default async function translateHtml(html, targetLang = 'hi') {
  if (typeof window === 'undefined' || !html) return html;

  try {
    // Fast path: the whole document fits in one request — no parsing, exact fidelity.
    if (html.length <= MAX_BATCH_CHARS) {
      const [translated] = await translateChunks([html], targetLang);
      return translated || html;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="__t_root">${html}</div>`, 'text/html');
    const root = doc.getElementById('__t_root');

    const chunks = [];
    collectChunks(Array.from(root.childNodes), MAX_BATCH_CHARS, chunks);

    const results = chunks.map((c) => c.html); // default to original (covers passthrough)
    const translatableIdx = chunks
      .map((c, i) => (c.translate ? i : -1))
      .filter((i) => i >= 0);
    const batches = buildBatches(
      translatableIdx,
      (i) => chunks[i].html.length,
      MAX_BATCH_CHARS,
    );

    await runWithConcurrency(
      batches,
      async (indices) => {
        try {
          const out = await translateChunks(indices.map((i) => chunks[i].html), targetLang);
          indices.forEach((idx, k) => { results[idx] = out[k] ?? chunks[idx].html; });
        } catch {
          // On failure leave this batch's chunks in their original language.
        }
      },
      CONCURRENCY,
    );

    return results.join('');
  } catch {
    return html;
  }
}
